import useAuth from '@app/auth/use-auth';
import ReportContent from '@app/components/reports/components/report-content';
import ReportMetric from '@app/components/reports/components/report-metric';
import SimpleBarChart from '@shared/components/chart/simple-bar-chart';
import Text from '@shared/components/content/text';
import { ChartConfig } from '@shared/components/ui/chart';
import { useCCFReportResult } from '../../ccf-report-result-context';

export default function Scope3() {
  const {
    result: { scope3 },
  } = useCCFReportResult();

  const { user } = useAuth();
  const scope3Supported = user?.features.scope3;

  const data = {
    scope3_emissions: Number(scope3.totalScopeEmissionsTonnes),
    pct_of_total_emissions: Number(scope3.pctOfCompanyEmissions),
    emissions_by_category: scope3.categories
      .filter((category) => category.status === 'included')
      .map((category) => {
        return {
          category: category.name,
          emissions: category.totalCategoryEmissionsTonnes,
          pct: category.pctOfScopeEmissions,
          fill: 'var(--chart-3)',
        };
      }),
  };

  const chartConfig = {
    emissions: {
      label: 'Emissions',
      unit: 'tCO2e',
    },
    'Fuel and energy-related activities': {
      label: 'Fuel and energy-related activities',
      color: 'var(--chart-1)',
      unit: 'tCO2e',
    },
    'Purchased goods and services': {
      label: 'Purchased goods and services',
      color: 'var(--chart-3)',
      unit: 'tCO2e',
    },
  } satisfies ChartConfig;

  return (
    <ReportContent>
      <>
        {!scope3Supported ? (
          <>
            <Text variant="reportBody">
              Scope 3 is the broadest category, covering 15 categories: all
              other indirect emissions across your value chain, both upstream
              and downstream
            </Text>
            <Text variant="reportBody">
              For most food businesses this category accounts for over 90% of
              their total emissions.
            </Text>

            <ReportMetric
              title="Scope 3 emissions"
              value="Not included in this report"
              size="lg"
              valueClassName="text-base"
              className="my-8"
            />
          </>
        ) : (
          <>
            <Text variant="reportBody">
              Scope 3 includes all other activities from your value chain,
              separated into 15 categories. These categories cover both upstream
              and downstream activities. For food & drink companies, the
              majority of your emissions usually comes from your purchased food
              & drink.
            </Text>
            <ReportMetric
              title="Scope 3 emissions"
              value={data.scope3_emissions}
              unit="tCO2e"
              size="lg"
              className="my-8"
            />
            <ReportMetric
              title="Share of total emissions"
              value={data.pct_of_total_emissions}
              unit="%"
              size="md"
              className="my-8"
            />
          </>
        )}
      </>
      <>
        {!scope3Supported ? (
          <Text variant="h3">Report Scope 3 with My Emissions</Text>
        ) : (
          <>
            <Text variant="h3">Scope 3 emissions by category</Text>
            <SimpleBarChart
              nameKey="category"
              dataKey="emissions"
              chartData={data.emissions_by_category}
              chartConfig={chartConfig}
              className="flex h-96 w-full"
              layout="vertical"
              margin={{ right: 80, left: 40 }}
              XAxisProps={{
                label: {
                  value: 'tCO2e',
                  position: 'right',
                  offset: 20,
                },
              }}
            />
          </>
        )}
      </>
    </ReportContent>
  );
}
